import React, { useState, useEffect, useCallback } from 'react';

import { reportError } from '@this/lib/bugsnag';
import type SearchQuery from '../../../../domain/search_query';
import type SearchQueryItem from '../../../../domain/search_query_item';
import type SearchResultItem from '../../../../domain/search_result_item';
import type SelectRepository from '../../../../domain/select_repository';
import type SelectStore from '../../../../domain/select_store';
import type HotelList from '../../../../domain/hotel/hotel_list';
import type FlightList from '../../../../domain/flight/flight_list';
import TransitList from '../../../../domain/transit/transit_list';

import ListAreaLayout from './ListAreaLayout';

export interface SelectListAreaProps {
  // Select.props
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  serviceId: number;

  // SelectListArea.props
  icon: string;
  listWrapper: TransitList | HotelList | FlightList | null;
  type: string | undefined;
  subtype: number | undefined;
  show: boolean;
  queryItem?: SearchQueryItem;
  resultItem: SearchResultItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
}

export interface SelectListAreaState {
  transportSegment: number | undefined;
  isOpenFlightCondition: boolean;
  errors: string[];
}

const SelectListArea: React.FC<SelectListAreaProps> = ({
  query,
  store,
  repository,
  serviceId,
  icon,
  listWrapper,
  type,
  subtype,
  show,
  queryItem,
  resultItem,
  direction,
  showSearchBox,
  toggleShowSearchBox
}) => {
  const [transportSegment, setTransportSegment] = useState<number | undefined>(listWrapper?.segment);
  const [isOpenFlightCondition, setIsOpenFlightCondition] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setTransportSegment(listWrapper?.segment);
  }, [listWrapper]);

  const handleTransportSegmentClick = useCallback(
    (i: number) => () => {
      if (listWrapper) {
        listWrapper.segment = i;
        setTransportSegment(i);
      }
    },
    [listWrapper]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!query) return;

      try {
        query.validationPromise();
        if (!query || !queryItem || !resultItem) return;

        query.withAutoInputItemsSearch(
          () => {
            resultItem?.setLoading(true);
            if (queryItem) repository.startPartialSearch(queryItem);
          },
          () => {
            window.history.replaceState(null, '', `${location.pathname}${query.getQueryString()}`);
          }
        );
      } catch (err) {
        setErrors(err as string[]);
      }
    },
    [query, queryItem, resultItem, repository]
  );

  const handleFlightConditionSubmit = useCallback(
    (
      filteredUpgradeSeat: boolean,
      carrierIds: string[],
      changeablePrices: string[],
      showConnectingAir: boolean
    ) => {
      if (queryItem) {
        queryItem.setFilteredUpgradeSeat(filteredUpgradeSeat);
        queryItem.setCarrierIds(carrierIds);
        queryItem.setChangeablePrices(changeablePrices);
        queryItem.setShowConnectingAir(showConnectingAir);
      }
      if (listWrapper && listWrapper instanceof TransitList) {
        listWrapper.handleFilteredUpgradeSeat(filteredUpgradeSeat);
        listWrapper.handleAirlinesChange(carrierIds);
        listWrapper.handleChangeablePriceChange(changeablePrices);
        listWrapper.handleShowConnectingAir(showConnectingAir);
      }
      setIsOpenFlightCondition(false);
      app.render();
    },
    [queryItem, listWrapper]
  );

  const openFlightCondition = useCallback(() => setIsOpenFlightCondition(true), []);
  const closeFlightCondition = useCallback(() => setIsOpenFlightCondition(false), []);

  try {
    return (
      <ListAreaLayout
        query={query}
        store={store}
        repository={repository}
        serviceId={serviceId}
        icon={icon}
        listWrapper={listWrapper}
        type={type}
        subtype={subtype}
        show={show}
        queryItem={queryItem}
        resultItem={resultItem}
        direction={direction}
        showSearchBox={showSearchBox}
        transportSegment={transportSegment}
        errors={errors}
        handleTransportSegmentClick={handleTransportSegmentClick}
        toggleShowSearchBox={toggleShowSearchBox}
        handleSubmit={handleSubmit}
        handleFlightConditionSubmit={handleFlightConditionSubmit}
        openFlightCondition={openFlightCondition}
        closeFlightCondition={closeFlightCondition}
        isOpenFlightCondition={isOpenFlightCondition}
      />
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default SelectListArea;
